import type { Locale, Market } from "@/lib/site";

export type Distributor = {
  name: string;
  address: string;
  email: string;
  phones?: string[];
  description?: string;
  website?: { label: string; href: string };
  localized?: Partial<Record<Locale, { name?: string; address?: string; description?: string }>>;
};

export type ContactConfig =
  | {
      variant: "exclusive-distributor";
      distributor: Distributor;
      otherDistributors: Distributor[];
      directSalesContact?: Distributor;
    }
  | { variant: "distributor-list"; distributors: Distributor[]; directSalesContact?: Distributor };

const romsatUkraine: Distributor = {
  name: "РОМСАТ",
  description:
    "РОМСАТ — стратегічний партнер і ексклюзивний дистриб’ютор продукції inext в Україні. Компанія входить до провідних українських дистриб’юторів телекомунікаційного, мережевого та телевізійного обладнання, працює з широким портфелем світових брендів і забезпечує партнерам вигідні комерційні умови, технічну підтримку, допомогу з проєктуванням та логістику по країні.",
  address: "03142, Україна, м. Київ, вул. Василя Стуса, 35-37",
  phones: ["+380443945454", "+380442255454"],
  email: "info@romsat.ua",
  website: { label: "romsat.ua", href: "https://romsat.ua" },
};

const romsatGlobal: Distributor = {
  ...romsatUkraine,
  name: "ROMSAT - official inext distributor in Ukraine",
  description:
    "ROMSAT is one of the leading Ukrainian distributors of telecommunications, network, and television equipment, with a robust distribution network spanning across Ukraine. The distributor offers an extensive portfolio of products from top global brands, including: inext, Edge-Core, Totolink, Teltonika, Hanwha Vision, ITC, Magner, and many others. ROMSAT provides its partners with exceptional terms and conditions, comprehensive technical support, project design assistance, and efficient nationwide logistics.",
  localized: {
    uk: {
      name: "РОМСАТ — офіційний дистриб’ютор inext в Україні",
      description:
        "РОМСАТ — стратегічний партнер і офіційний дистриб’ютор inext в Україні. Компанія входить до провідних українських дистриб’юторів телекомунікаційного, мережевого та телевізійного обладнання, працює з широким портфелем світових брендів, включно з inext, Edge-Core, Totolink, Teltonika, Hanwha Vision, ITC, Magner та іншими, і забезпечує партнерам вигідні комерційні умови, технічну підтримку, допомогу з проєктуванням та логістику по країні.",
    },
    pl: {
      name: "ROMSAT — oficjalny dystrybutor inext w Ukrainie",
      description:
        "ROMSAT to jeden z czołowych ukraińskich dystrybutorów sprzętu telekomunikacyjnego, sieciowego i telewizyjnego, z rozbudowaną siecią dostaw w całej Ukrainie. Firma oferuje portfolio globalnych marek, w tym inext, Edge-Core, Totolink, Teltonika, Hanwha Vision, ITC, Magner i wiele innych, zapewniając partnerom warunki handlowe, wsparcie techniczne, pomoc projektową i logistykę krajową.",
    },
    pt: {
      name: "ROMSAT — distribuidor oficial da inext na Ucrânia",
      description:
        "A ROMSAT é um dos principais distribuidores ucranianos de equipamentos de telecomunicações, rede e televisão, com uma sólida rede de distribuição em toda a Ucrânia. O distribuidor oferece um amplo portfólio das principais marcas globais, incluindo inext, Edge-Core, Totolink, Teltonika, Hanwha Vision, ITC, Magner e muitas outras, e garante aos parceiros condições comerciais vantajosas, suporte técnico completo, apoio em projetos e logística em todo o país.",
    },
    es: {
      name: "ROMSAT — distribuidor oficial de inext en Ucrania",
      description:
        "ROMSAT es uno de los principales distribuidores ucranianos de equipos de telecomunicaciones, redes y televisión, con una sólida red de distribución en toda Ucrania. El distribuidor ofrece un amplio portafolio de las principales marcas globales, incluidas inext, Edge-Core, Totolink, Teltonika, Hanwha Vision, ITC, Magner y muchas otras, y brinda a sus socios condiciones comerciales excepcionales, soporte técnico integral, asistencia en el diseño de proyectos y logística eficiente a nivel nacional.",
    },
  },
};

const cdr: Distributor = {
  name: "CDR - official distributor of inext in Poland",
  description:
    "CDR.pl is one of the largest distributors of telecommunications equipment in Europe, with warehouses in Poland, Italy, and Spain. The distributor offers products from the following brands: inext, Huawei, Ubiquiti, MikroTik, RF Elements, Mimosa, TP-Link, Mercusys, BBS Power, Dahua, KNIPEX, D-Link, Solarix, Teltonika, YOFC, Opton, and many others. CDR provides its customers with the best terms and conditions, including same-day delivery and a door-to-door guarantee.",
  address: "Pałki Street 15, 44-240 Żory, Poland",
  phones: ["+48 32 750 5000"],
  email: "sales@cdr.pl",
  website: { label: "en.cdr.pl", href: "https://en.cdr.pl" },
  localized: {
    uk: {
      name: "CDR — офіційний дистриб’ютор inext у Польщі",
      address: "Pałki Street 15, 44-240 Żory, Польща",
      description:
        "CDR.pl — один із найбільших дистриб’юторів телекомунікаційного обладнання в Європі зі складами у Польщі, Італії та Іспанії. Компанія пропонує продукцію inext, Huawei, Ubiquiti, MikroTik, RF Elements, Mimosa, TP-Link, Mercusys, BBS Power, Dahua, KNIPEX, D-Link, Solarix, Teltonika, YOFC, Opton та інших брендів, а також забезпечує вигідні умови, швидке відвантаження й доставку до дверей.",
    },
    pl: {
      name: "CDR — oficjalny dystrybutor inext w Polsce",
      address: "ul. Pałki 15, 44-240 Żory, Polska",
      description:
        "CDR.pl to jeden z największych dystrybutorów sprzętu telekomunikacyjnego w Europie, z magazynami w Polsce, Włoszech i Hiszpanii. Firma oferuje produkty marek takich jak inext, Huawei, Ubiquiti, MikroTik, RF Elements, Mimosa, TP-Link, Mercusys, BBS Power, Dahua, KNIPEX, D-Link, Solarix, Teltonika, YOFC i Opton. Zapewnia klientom korzystne warunki handlowe, szybką wysyłkę i dostawę pod wskazany adres.",
    },
    pt: {
      name: "CDR — distribuidor oficial da inext na Polônia",
      address: "Pałki Street 15, 44-240 Żory, Polônia",
      description:
        "A CDR.pl é um dos maiores distribuidores de equipamentos de telecomunicações da Europa, com armazéns na Polônia, na Itália e na Espanha. O distribuidor oferece produtos das marcas inext, Huawei, Ubiquiti, MikroTik, RF Elements, Mimosa, TP-Link, Mercusys, BBS Power, Dahua, KNIPEX, D-Link, Solarix, Teltonika, YOFC, Opton e muitas outras, além de garantir aos clientes as melhores condições, incluindo entrega no mesmo dia e garantia de entrega porta a porta.",
    },
    es: {
      name: "CDR — distribuidor oficial de inext en Polonia",
      address: "Pałki Street 15, 44-240 Żory, Polonia",
      description:
        "CDR.pl es uno de los mayores distribuidores de equipos de telecomunicaciones de Europa, con almacenes en Polonia, Italia y España. El distribuidor ofrece productos de las marcas inext, Huawei, Ubiquiti, MikroTik, RF Elements, Mimosa, TP-Link, Mercusys, BBS Power, Dahua, KNIPEX, D-Link, Solarix, Teltonika, YOFC, Opton y muchas otras, y garantiza a sus clientes las mejores condiciones, incluidas la entrega el mismo día y la garantía de entrega puerta a puerta.",
    },
  },
};

const inextEurope: Distributor = {
  name: "INEXT EUROPE s.r.o.",
  description:
    "INEXT EUROPE s.r.o. is the official inext European legal entity supporting certifications, imports, regional operations, and direct partner sales in markets not covered by a local distributor.",
  address: "Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia",
  email: "info@inext.ltd",
  localized: {
    uk: {
      description:
        "INEXT EUROPE s.r.o. — офіційна європейська юридична особа inext, яка підтримує сертифікації, імпорт, регіональні операції та прямі партнерські продажі на ринках без локального дистриб’ютора.",
      address: "Ligurčeková 8, 821 06 Podunajské, Bratislava, Словаччина",
    },
    pl: {
      description:
        "INEXT EUROPE s.r.o. to oficjalny europejski podmiot prawny inext, wspierający certyfikacje, import, operacje regionalne i bezpośrednią sprzedaż partnerską na rynkach bez lokalnego dystrybutora.",
      address: "Ligurčeková 8, 821 06 Podunajské, Bratislava, Słowacja",
    },
    pt: {
      description:
        "A INEXT EUROPE s.r.o. é a entidade jurídica europeia oficial da inext, responsável por certificações, importação, operações regionais e vendas diretas a parceiros em mercados sem distribuidor local.",
      address: "Ligurčeková 8, 821 06 Podunajské, Bratislava, Eslováquia",
    },
    es: {
      description:
        "INEXT EUROPE s.r.o. es la entidad jurídica europea oficial de inext, encargada de certificaciones, importaciones, operaciones regionales y ventas directas a socios en mercados sin distribuidor local.",
      address: "Ligurčeková 8, 821 06 Podunajské, Bratislava, Eslovaquia",
    },
  },
};

const digimax: Distributor = {
  name: "Digimax - official inext distributor in Moldova",
  description:
    "DIGIMAX was founded in 2006. The company’s main operations are the supply of equipment for receiving satellite TV signals, equipment and accessories for cable TV networks, including FTTH, FTTB, FTTC optical lines, as well as the integration of technologies for cable TV networks, OTT and IPTV, and mobile telephony networks. Over 18 years, DIGIMAX has formed a wide network of dealers and customers nationwide.",
  address: "14/3 Gradina Botanica street, Chisinau, Moldova",
  phones: ["+373 22 522 599", "+373 22 639 004"],
  email: "info@digimax.md",
  website: { label: "digimax.md", href: "https://digimax.md" },
  localized: {
    uk: {
      name: "Digimax — офіційний дистриб’ютор inext у Молдові",
      address: "вул. Gradina Botanica 14/3, Кишинів, Молдова",
      description:
        "DIGIMAX працює з 2006 року та постачає обладнання для прийому супутникового ТБ, обладнання й аксесуари для кабельних мереж, зокрема FTTH, FTTB і FTTC, а також інтегрує технології для кабельного ТБ, OTT, IPTV і мобільних мереж. За роки роботи компанія сформувала широку мережу дилерів і клієнтів по всій країні.",
    },
    pl: {
      name: "Digimax — oficjalny dystrybutor inext w Mołdawii",
      address: "ul. Gradina Botanica 14/3, Kiszyniów, Mołdawia",
      description:
        "DIGIMAX działa od 2006 roku i dostarcza sprzęt do odbioru telewizji satelitarnej, urządzenia i akcesoria dla sieci kablowych, w tym FTTH, FTTB i FTTC, a także integruje technologie dla telewizji kablowej, OTT, IPTV i sieci mobilnych. Przez lata firma zbudowała szeroką sieć dealerów i klientów w całym kraju.",
    },
    pt: {
      name: "Digimax — distribuidor oficial da inext na Moldávia",
      address: "Rua Gradina Botanica, 14/3, Chisinau, Moldávia",
      description:
        "A DIGIMAX foi fundada em 2006. As principais atividades da empresa são o fornecimento de equipamentos para recepção de sinais de TV via satélite e de equipamentos e acessórios para redes de TV a cabo — incluindo linhas ópticas FTTH, FTTB e FTTC —, além da integração de tecnologias para redes de TV a cabo, OTT e IPTV e para redes de telefonia móvel. Ao longo de 18 anos, a DIGIMAX construiu uma ampla rede de revendedores e clientes em todo o país.",
    },
    es: {
      name: "Digimax — distribuidor oficial de inext en Moldavia",
      address: "Calle Gradina Botanica, 14/3, Chisinau, Moldavia",
      description:
        "DIGIMAX fue fundada en 2006. Las principales actividades de la empresa son el suministro de equipos para la recepción de señales de TV satelital y de equipos y accesorios para redes de TV por cable —incluidas líneas ópticas FTTH, FTTB y FTTC—, además de la integración de tecnologías para redes de TV por cable, OTT e IPTV y para redes de telefonía móvil. A lo largo de 18 años, DIGIMAX ha construido una amplia red de distribuidores y clientes en todo el país.",
    },
  },
};

export const contactConfig: Record<Market, ContactConfig> = {
  global: {
    variant: "distributor-list",
    distributors: [cdr, romsatGlobal, digimax],
    directSalesContact: inextEurope,
  },
  ukraine: {
    variant: "exclusive-distributor",
    distributor: romsatUkraine,
    otherDistributors: [cdr, digimax],
    directSalesContact: inextEurope,
  },
  poland: {
    variant: "exclusive-distributor",
    distributor: cdr,
    otherDistributors: [romsatGlobal, digimax],
    directSalesContact: inextEurope,
  },
  // TODO(pt-BR): replace with a real Brazil distributor/contact when available.
  // Stubbed to mirror the global market so /br renders a valid contacts page.
  brazil: {
    variant: "distributor-list",
    distributors: [cdr, romsatGlobal, digimax],
    directSalesContact: inextEurope,
  },
  // TODO(es-419): replace with a real Latin America distributor/contact when available.
  // Stubbed to mirror the global market so /es renders a valid contacts page.
  latam: {
    variant: "distributor-list",
    distributors: [cdr, romsatGlobal, digimax],
    directSalesContact: inextEurope,
  },
};
